import { useState, type FormEvent } from 'react';
import type { Rol } from '@medchoice/shared';
import { ApiError } from '../../../api/client';
import { publico } from '../../../api/publico';
import { usePedido } from '../../../api/usePedido';
import { Enlace, irA } from '../../../router';
import type { ScreenProps } from '../../../routes/types';
import { useSesion } from '../../../session/Sesion';
import { AvisoError, Boton, Entrada, Marco } from '../../../ui';
import { useFormulario } from '../../../forms/useFormulario';
import { LARGOS, requerido, validarEmail } from '../../../forms/validacion';
import { AvisoDeContexto, CampoClave, destinoDespuesDeIngresar } from './piezas';

// El profesor pidió poder entrar a la demostración sin tipear credenciales. Las cuentas
// las da la API, así que si la demo está apagada los botones no aparecen.
const TEXTO_ROL: Record<Rol, string> = { paciente: 'Paciente', profesional: 'Profesional', administrador: 'Administración' };
const ORDEN_ROL: Rol[] = ['paciente', 'profesional', 'administrador'];

export function Ingreso({ consulta }: ScreenProps) {
  const { ingresar } = useSesion();
  const { datos: demo } = usePedido(() => publico.cuentasDeDemostracion(), []);
  const accesos = ORDEN_ROL.map((rol) => demo?.find((c) => c.rol === rol)).filter((c) => c !== undefined);
  const volverA = consulta.get('volverA');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const formulario = useFormulario(
    { email: '', clave: '' },
    { email: validarEmail, clave: requerido('La contraseña') },
    { email: LARGOS.email, clave: LARGOS.clave },
  );

  const entrar = async (email: string, clave: string) => {
    setError(null);
    setEnviando(true);
    try {
      const usuario = await ingresar(email, clave);
      irA(destinoDespuesDeIngresar(usuario.rol, volverA), { reemplazar: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos conectar con MedChoice. Probá de nuevo en un momento.');
      setEnviando(false);
    }
  };

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (formulario.validarTodo()) void entrar(formulario.valores.email.trim(), formulario.valores.clave);
  };

  return (
    <Marco
      titulo="Entrá a MedChoice"
      bajada="Con tu cuenta ves tus turnos, reservás en segundos y dejás tus reseñas."
      pie={<>¿No tenés cuenta? <Enlace a="/registro">Registrate</Enlace></>}
    >
      <AvisoDeContexto volverA={volverA} />
      <form onSubmit={enviar} noValidate className="acceso__formulario">
        <AvisoError>{error}</AvisoError>
        <Entrada etiqueta="Correo electrónico" type="email" autoComplete="email" required {...formulario.campo('email')} />
        <CampoClave etiqueta="Contraseña" autoComplete="current-password" required {...formulario.campo('clave')} />
        <Enlace a="/recuperar" className="acceso__olvido">¿Olvidaste tu contraseña?</Enlace>
        <Boton type="submit" tamano="grande" anchoCompleto cargando={enviando}>Ingresar</Boton>
      </form>

      {accesos.length > 0 && (
        <div className="acceso__demo">
          <span>Acceso rápido para la demostración</span>
          <div className="acceso__demo-botones">
            {accesos.map(({ rol, email, clave, descripcion }) => (
              <Boton key={email} variante="secundario" tamano="chico" disabled={enviando} title={descripcion}
                onClick={() => void entrar(email, clave)}>
                {TEXTO_ROL[rol]}
              </Boton>
            ))}
          </div>
        </div>
      )}
    </Marco>
  );
}
